import { useId, useState } from "react";
import { Field, FieldLabel, Select } from "./AdminForm.jsx";

/**
 * Line-item editor — the scope-of-work table shared by contracts (4b-1) and
 * the proposal builder (4b-2). The editor is deliberately dumb about storage:
 * the parent supplies async handlers, so the same component drives
 *   - local mode (New Contract modal: handlers mutate component state), and
 *   - API mode (draft contract editing: handlers hit /api/contracts/:id/line-items).
 *
 * Props:
 *   items    : line items in API shape ({ id, serviceName, feeType, amount, … })
 *   services : active catalog services ({ id, name, category, … }) for the
 *              catalog picker — fetched by the parent, since the editor is
 *              storage-agnostic and the New Contract modal has no proposal/contract yet
 *   editable : false renders a read-only table (no add/edit/delete affordances)
 *   onCreate : async (values) => void
 *   onUpdate : async (id, values) => void
 *   onRemove : async (id) => void
 */

export const FEE_TYPES = [
  { value: "one_time",          label: "One-time" },
  { value: "monthly_recurring", label: "Monthly recurring" },
  { value: "quarterly_recurring", label: "Quarterly recurring" },
  { value: "annual_recurring",  label: "Annual recurring" },
  { value: "variable_monthly",  label: "Variable monthly" },
  { value: "milestone_deposit", label: "Milestone · deposit" },
  { value: "milestone_balance", label: "Milestone · balance" },
  { value: "per_unit",          label: "Per unit" },
];

export const LINE_ITEM_CATEGORIES = [
  "website", "social", "email", "ads", "hosting", "seo", "content", "software", "licensing", "other",
];

const DATA_SOURCES = [
  { value: "google_ads_spend", label: "Google Ads spend" },
  { value: "manual",           label: "Manual" },
];

const MILESTONE_CONDITIONS = [
  { value: "on_signing",          label: "On signing" },
  { value: "on_project_complete", label: "On project complete" },
  { value: "on_delivery",         label: "On delivery" },
];

const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/** Human summary of a line's fee — "how this line bills" in one cell. */
export function feeSummary(li) {
  switch (li.feeType) {
    case "one_time":
      return `${fmtMoney(li.amount)} one-time`;
    case "monthly_recurring":
      return `${fmtMoney(li.amount)}/mo`;
    case "quarterly_recurring":
      return `${fmtMoney(li.amount)}/qtr`;
    case "annual_recurring":
      return `${fmtMoney(li.amount)}/yr`;
    case "variable_monthly": {
      const pct = li.variableRate != null ? `${Math.round(li.variableRate * 10000) / 100}%` : "—";
      const base = li.variableDataSource === "google_ads_spend" ? "ad spend" : "base";
      const min = li.variableMinimum != null ? ` (min ${fmtMoney(li.variableMinimum)})` : "";
      return li.amount != null ? `${fmtMoney(li.amount)}/mo` : `${pct} of ${base}${min}/mo`;
    }
    case "milestone_deposit":
    case "milestone_balance": {
      if (li.amount != null) return fmtMoney(li.amount);
      const pct = li.milestonePercent ?? 0;
      const total = li.projectTotal ?? 0;
      return `${pct}% of ${fmtMoney(total)} = ${fmtMoney((total * pct) / 100)}`;
    }
    case "per_unit":
      return `${fmtMoney(li.amount)} / unit`;
    default:
      return "—";
  }
}

// Change #4 — the linkage decision is REQUIRED and has no default. A silently
// defaulted "custom" would recreate the exact ambiguity this change removes,
// one value along: it would mean "the operator didn't look at the picker"
// while claiming "the operator decided nothing applies".
const EMPTY_FORM = {
  serviceLink: "", serviceId: "",
  serviceName: "", description: "", category: "", feeType: "monthly_recurring",
  amount: "", variableRate: "", variableMinimum: "", variableDataSource: "google_ads_spend",
  milestonePercent: "", projectTotal: "", milestoneCondition: "on_signing",
  billingCycleDay: "", annualBillingMonth: "", annualBillingDay: "",
};

function formFromItem(li) {
  return {
    // A recorded catalog or custom choice is preserved. `unlinked` (legacy,
    // AI intake) is NOT a choice — the operator must make one on the next save.
    serviceLink: li.serviceLink === "catalog" || li.serviceLink === "custom" ? li.serviceLink : "",
    serviceId: li.serviceId ?? "",
    serviceName: li.serviceName ?? "",
    description: li.description ?? "",
    category: li.category ?? "",
    feeType: li.feeType ?? "monthly_recurring",
    amount: li.amount ?? "",
    variableRate: li.variableRate != null ? String(li.variableRate * 100) : "",
    variableMinimum: li.variableMinimum ?? "",
    variableDataSource: li.variableDataSource ?? "google_ads_spend",
    milestonePercent: li.milestonePercent ?? "",
    projectTotal: li.projectTotal ?? "",
    milestoneCondition: li.milestoneCondition ?? "on_signing",
    billingCycleDay: li.billingCycleDay ?? "",
    annualBillingMonth: li.annualBillingMonth ?? "",
    annualBillingDay: li.annualBillingDay ?? "",
  };
}

// Fields owned by each fee type. Everything else fee-related gets cleared on
// update, so switching a line's fee type can't leave stale numbers behind.
const FEE_FIELDS = [
  "amount", "variableRate", "variableMinimum", "variableDataSource",
  "milestonePercent", "projectTotal", "milestoneCondition",
  "billingCycleDay", "annualBillingMonth", "annualBillingDay",
];

// Form strings → API payload. `clearUnused` (updates) sends explicit nulls
// for fee fields the selected type doesn't use; creates just omit them.
function payloadFromForm(f, { clearUnused = false } = {}) {
  const num = (v) => (v === "" || v == null ? undefined : Number(v));
  const fee = {};
  switch (f.feeType) {
    case "one_time":
    case "per_unit":
      fee.amount = num(f.amount);
      break;
    case "monthly_recurring":
      fee.amount = num(f.amount);
      fee.billingCycleDay = num(f.billingCycleDay);
      break;
    case "quarterly_recurring":
      fee.amount = num(f.amount);
      fee.billingCycleDay = num(f.billingCycleDay);
      break;
    case "annual_recurring":
      fee.amount = num(f.amount);
      fee.annualBillingMonth = num(f.annualBillingMonth);
      fee.annualBillingDay = num(f.annualBillingDay);
      break;
    case "variable_monthly":
      fee.variableRate = num(f.variableRate) != null ? num(f.variableRate) / 100 : undefined;
      fee.variableMinimum = num(f.variableMinimum);
      fee.variableDataSource = f.variableDataSource;
      fee.billingCycleDay = num(f.billingCycleDay);
      break;
    case "milestone_deposit":
    case "milestone_balance":
      fee.milestonePercent = num(f.milestonePercent);
      fee.projectTotal = num(f.projectTotal);
      fee.milestoneCondition = f.milestoneCondition;
      break;
    default:
      break;
  }
  if (clearUnused) {
    for (const k of FEE_FIELDS) if (fee[k] === undefined) fee[k] = null;
  } else {
    for (const k of Object.keys(fee)) if (fee[k] === undefined) delete fee[k];
  }
  return {
    // Change #4: the pair travels together. A custom line OMITS serviceId (the
    // API refuses a null there and derives the null itself); it never sends
    // `unlinked`, which is not a choice a caller may make.
    ...(f.serviceLink === "catalog"
      ? { serviceLink: "catalog", serviceId: f.serviceId }
      : { serviceLink: "custom" }),
    serviceName: f.serviceName.trim(),
    description: f.description.trim() ? f.description.trim() : clearUnused ? null : undefined,
    category: f.category ? f.category : clearUnused ? null : undefined,
    feeType: f.feeType,
    ...fee,
  };
}

function validateForm(f) {
  if (f.serviceLink !== "catalog" && f.serviceLink !== "custom") {
    return "Choose a catalog service, or mark this line custom";
  }
  if (f.serviceLink === "catalog" && !f.serviceId) return "Pick the catalog service";
  if (!f.serviceName.trim()) return "Service name is required";
  const needsAmount = ["one_time", "per_unit", "monthly_recurring", "quarterly_recurring", "annual_recurring"].includes(f.feeType);
  if (needsAmount && !(Number(f.amount) >= 0 && f.amount !== "")) return "Amount is required";
  if (f.feeType === "variable_monthly" && f.variableRate === "" && f.variableMinimum === "") {
    return "A variable line needs a rate and/or a minimum";
  }
  if (["milestone_deposit", "milestone_balance"].includes(f.feeType)) {
    if (!(Number(f.milestonePercent) > 0)) return "Milestone % is required";
    if (!(Number(f.projectTotal) > 0)) return "Project total is required";
  }
  return null;
}

export function LineItemEditor({ items, editable = true, services = [], onCreate, onUpdate, onRemove }) {
  const linkGroup = useId();                    // one radio group per editor instance
  const [form, setForm] = useState(null);       // null = closed; {} = adding
  const [editingId, setEditingId] = useState(null); // line id being edited, or null
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  function openAdd() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setError(null);
  }
  function openEdit(li) {
    setEditingId(li.id);
    setForm(formFromItem(li));
    setError(null);
  }
  function close() {
    setForm(null);
    setEditingId(null);
    setError(null);
  }

  async function save() {
    const invalid = validateForm(form);
    if (invalid) return setError(new Error(invalid));
    setBusy(true);
    setError(null);
    try {
      if (editingId) await onUpdate(editingId, payloadFromForm(form, { clearUnused: true }));
      else await onCreate(payloadFromForm(form));
      close();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    setBusy(true);
    setError(null);
    try {
      await onRemove(id);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {items.length === 0 && !form ? (
        <p className="py-3 text-[12px] text-text-muted">No line items yet.</p>
      ) : (
        items.length > 0 && (
          <table className="w-full text-[12px] border-collapse" style={{ fontFeatureSettings: "'tnum'" }}>
            <thead>
              <tr className="text-left text-text-muted text-[11px] uppercase border-b border-border-light" style={{ letterSpacing: "0.5px" }}>
                <th className="py-2 pr-3 font-medium">Service</th>
                <th className="py-2 pr-3 font-medium">Category</th>
                <th className="py-2 pr-3 font-medium text-right">Fee</th>
                {editable && <th className="py-2 font-medium" />}
              </tr>
            </thead>
            <tbody>
              {items.map((li) => (
                <tr key={li.id} className="border-b border-border-light">
                  <td className="py-2 pr-3">
                    <div className="text-text-primary font-medium">
                      {li.serviceName}
                      {li.serviceLink && (
                        <span className="ml-1.5 text-[10px] uppercase text-text-muted font-normal" style={{ letterSpacing: "0.5px" }}>
                          {li.serviceLink === "catalog" ? "Catalog" : li.serviceLink === "custom" ? "Custom" : "Unlinked"}
                        </span>
                      )}
                    </div>
                    {li.description && <div className="text-[11px] text-text-muted mt-0.5">{li.description}</div>}
                  </td>
                  <td className="py-2 pr-3 text-text-secondary capitalize">{li.category || "—"}</td>
                  <td className="py-2 pr-3 text-text-secondary text-right whitespace-nowrap">{feeSummary(li)}</td>
                  {editable && (
                    <td className="py-2 text-right whitespace-nowrap">
                      <button type="button" onClick={() => openEdit(li)}
                        className="text-[12px] text-accent hover:underline font-medium">Edit</button>
                      <button type="button" disabled={busy} onClick={() => remove(li.id)}
                        className="ml-2.5 text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50">Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {editable && !form && (
        <button type="button" onClick={openAdd}
          className="mt-2 text-[12px] text-accent hover:underline font-medium">
          + Add line item
        </button>
      )}

      {form && (
        <div className="mt-3 rounded-card border border-border-default p-3 space-y-2.5 bg-app-bg">
          {/* Change #4 — the linkage decision. Two explicit choices, no default:
              a catalog service (the picker), or a custom line. Selecting a
              service may prefill an EMPTY service name only — serviceName is a
              commercial snapshot the operator owns, not a mirror of the catalog. */}
          <div>
            <FieldLabel>Catalog link<span className="text-negative ml-0.5">*</span></FieldLabel>
            <div className="flex flex-wrap items-center gap-4 text-[12px] text-text-secondary">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name={linkGroup} value="catalog" checked={form.serviceLink === "catalog"}
                  onChange={() => setForm((f) => ({ ...f, serviceLink: "catalog" }))} />
                Catalog service
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name={linkGroup} value="custom" checked={form.serviceLink === "custom"}
                  onChange={() => setForm((f) => ({ ...f, serviceLink: "custom", serviceId: "" }))} />
                Custom line
              </label>
            </div>
            {form.serviceLink === "catalog" && (
              <div className="mt-2">
                <Select label="Service" required value={form.serviceId}
                  onChange={(v) => setForm((f) => {
                    const svc = services.find((s) => s.id === v);
                    return { ...f, serviceId: v, serviceName: f.serviceName.trim() || !svc ? f.serviceName : svc.name };
                  })}
                  options={[
                    { value: "", label: "— pick a catalog service —" },
                    // An existing catalog id whose service is no longer in the active
                    // list stays understandable and preservable: it is offered as-is.
                    ...(form.serviceId && !services.some((s) => s.id === form.serviceId)
                      ? [{ value: form.serviceId, label: `Current catalog service (${form.serviceId}) — not in the active list` }]
                      : []),
                    ...services.map((s) => ({ value: s.id, label: `${s.name}${s.category ? ` · ${s.category}` : ""}` })),
                  ]} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Service name" required value={form.serviceName}
              onChange={(v) => setForm((f) => ({ ...f, serviceName: v }))} />
            <Select label="Fee type" value={form.feeType}
              onChange={(v) => setForm((f) => ({ ...f, feeType: v, milestoneCondition: v === "milestone_balance" ? "on_project_complete" : v === "milestone_deposit" ? "on_signing" : f.milestoneCondition }))}
              options={FEE_TYPES} />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <Select label="Category" value={form.category}
              onChange={(v) => setForm((f) => ({ ...f, category: v }))}
              options={[{ value: "", label: "—" }, ...LINE_ITEM_CATEGORIES.map((c) => ({ value: c, label: c }))]} />
            <Field label="Description" value={form.description}
              onChange={(v) => setForm((f) => ({ ...f, description: v }))} />
          </div>

          {/* Fee-type-dependent fields */}
          {["one_time", "per_unit"].includes(form.feeType) && (
            <Field label={form.feeType === "per_unit" ? "Amount per unit ($)" : "Amount ($)"} type="number" required
              value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} />
          )}
          {form.feeType === "monthly_recurring" && (
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Amount / month ($)" type="number" required value={form.amount}
                onChange={(v) => setForm((f) => ({ ...f, amount: v }))} />
              <Field label="Billing day (1–28)" type="number" value={form.billingCycleDay}
                onChange={(v) => setForm((f) => ({ ...f, billingCycleDay: v }))} />
            </div>
          )}
          {form.feeType === "quarterly_recurring" && (
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Amount / quarter ($)" type="number" required value={form.amount}
                onChange={(v) => setForm((f) => ({ ...f, amount: v }))} />
              <Field label="Billing day (1–28)" type="number" value={form.billingCycleDay}
                onChange={(v) => setForm((f) => ({ ...f, billingCycleDay: v }))} />
            </div>
          )}
          {form.feeType === "annual_recurring" && (
            <div className="grid grid-cols-3 gap-2.5">
              <Field label="Amount / year ($)" type="number" required value={form.amount}
                onChange={(v) => setForm((f) => ({ ...f, amount: v }))} />
              <Field label="Billing month (1–12)" type="number" value={form.annualBillingMonth}
                onChange={(v) => setForm((f) => ({ ...f, annualBillingMonth: v }))} />
              <Field label="Billing day (1–31)" type="number" value={form.annualBillingDay}
                onChange={(v) => setForm((f) => ({ ...f, annualBillingDay: v }))} />
            </div>
          )}
          {form.feeType === "variable_monthly" && (
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Rate (% of base)" type="number" value={form.variableRate}
                onChange={(v) => setForm((f) => ({ ...f, variableRate: v }))} />
              <Field label="Minimum / month ($)" type="number" value={form.variableMinimum}
                onChange={(v) => setForm((f) => ({ ...f, variableMinimum: v }))} />
              <Select label="Base data source" value={form.variableDataSource}
                onChange={(v) => setForm((f) => ({ ...f, variableDataSource: v }))}
                options={DATA_SOURCES} />
              <Field label="Billing day (1–28)" type="number" value={form.billingCycleDay}
                onChange={(v) => setForm((f) => ({ ...f, billingCycleDay: v }))} />
            </div>
          )}
          {["milestone_deposit", "milestone_balance"].includes(form.feeType) && (
            <div className="grid grid-cols-3 gap-2.5">
              <Field label="Milestone %" type="number" required value={form.milestonePercent}
                onChange={(v) => setForm((f) => ({ ...f, milestonePercent: v }))} />
              <Field label="Project total ($)" type="number" required value={form.projectTotal}
                onChange={(v) => setForm((f) => ({ ...f, projectTotal: v }))} />
              <Select label="Due" value={form.milestoneCondition}
                onChange={(v) => setForm((f) => ({ ...f, milestoneCondition: v }))}
                options={MILESTONE_CONDITIONS} />
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button type="button" disabled={busy} onClick={save}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
              {editingId ? "Save line" : "Add line"}
            </button>
            <button type="button" onClick={close}
              className="text-[12px] text-text-secondary hover:text-text-primary">Cancel</button>
          </div>
        </div>
      )}

      {error && <div className="mt-2 text-[11px] text-negative">{error.message || "Something went wrong."}</div>}
    </div>
  );
}
